import type { VacationRequest } from '../models/vacation.types';
import {
    CLIENT_ACTIONABLE_STATUSES,
    CLIENT_RESEND_COOLDOWN_DAYS,
} from '../../../../shared/vacations/clientApproval';

export { CLIENT_RESEND_COOLDOWN_DAYS };

function toDateOrNull(value: unknown): Date | null {
    if (!value) return null;
    const candidate = value as { toDate?: () => Date };
    if (typeof candidate.toDate === 'function') return candidate.toDate();
    if (value instanceof Date) return value;
    const parsed = new Date(value as string | number);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Resolve if the current user can resend an email to a specific client.
 * Returns two booleans: showAction (render the button at all?) and canResend (enabled?).
 *
 * **The backend is the authority.** This is cosmetic only — disable the button to improve UX.
 * Do not rely on these booleans to prevent invalid calls; the callable will reject invalid states.
 */
export function resolveClientResendAvailability(input: {
    email: string;
    request: VacationRequest;
    now?: Date;
}): { showAction: boolean; canResend: boolean } {
    const { email, request, now = new Date() } = input;

    // Hide the button if:
    // 1. Status closed (not PendingManager/PendingClient)
    // 2. A client has already approved
    const isOpenStatus = CLIENT_ACTIONABLE_STATUSES.includes(request.status);
    const isApproved = request.clientApproval?.status === 'Approved';

    const showAction = Boolean(isOpenStatus && !isApproved && request.clientEmails?.length);

    if (!showAction) {
        return { showAction: false, canResend: false };
    }

    // Find the last send time for this email
    const emailLower = email.toLowerCase();
    const clientInvite = request.clientInvites?.find(
        (inv) => inv.email.toLowerCase() === emailLower
    );
    const lastSentAt =
        toDateOrNull(clientInvite?.lastSentAt) ?? toDateOrNull(request.requestedAt);

    // Check cooldown
    let canResend = true;
    if (lastSentAt) {
        const cooldownUntil = new Date(lastSentAt);
        cooldownUntil.setUTCDate(cooldownUntil.getUTCDate() + CLIENT_RESEND_COOLDOWN_DAYS);
        canResend = now >= cooldownUntil;
    }

    return { showAction: true, canResend };
}
